import { Form, Input, Button, Typography, Card, Divider, App, Row, Col } from "antd";
import { useNavigate, Link } from "react-router-dom";
import { useState } from "react";
import API from "../../../services/api";
import { auth, provider } from "../../../firebase";
import { signInWithPopup } from "firebase/auth";
import { useAuth } from "../../../context/AuthContext";

import "./Register.css";

const { Title, Text } = Typography;

function Register() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [form] = Form.useForm();
  const { setUser } = useAuth();
  const { message } = App.useApp();

  const onFinish = async (values) => {
    if (!navigator.onLine) {
      message.error("You are offline. Check your internet connection.");
      return;
    }
    try {
      setLoading(true);
      message.destroy();

      const cleanedValues = {
        ...values,
        name: values.name.trim(),
        email: values.email.trim(),
        phone: values.phone.trim(),
      };

      await API.post("/users/register", cleanedValues);

      message.success("Registration successful");
      navigate("/login");
    } catch (error) {
      message.destroy();
      message.error(
        error.response?.data?.message ||
        "No internet connection. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    message.destroy();

    // Get all form values
    const values = form.getFieldsValue();

    // Check if any field is empty
    if (!values.name || !values.email || !values.phone || !values.password) {
      message.error("Please fill all the fields");
      return;
    }

    // Validate name (only letters and spaces)
    const nameRegex = /^(?!\s*$)[A-Za-z ]+$/;
    if (!nameRegex.test(values.name)) {
      message.error("Name should contain only letters");
      return;
    }

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(values.email)) {
      message.error("Enter a valid email");
      return;
    }

    // Validate phone (Indian mobile number)
    const phoneRegex = /^[6-9]\d{9}$/;
    if (!phoneRegex.test(values.phone)) {
      message.error("Contact number must be 10 digits and start with 6-9");
      return;
    }

    // Validate password
    if (values.password.length < 6) {
      message.error("Password must be at least 6 characters");
      return;
    }

    // If all validations pass, submit the form
    form.submit();
  };

  const onFinishFailed = () => {
    // This is kept empty to prevent default Ant Design error messages
  };

  const handleGoogleSignUp = async () => {
    try {
      setGoogleLoading(true);
      message.destroy();

      provider.setCustomParameters({
        prompt: "select_account",
      });

      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const token = await user.getIdToken();

      const res = await API.post(
        "/auth/google",
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        }
      );

      localStorage.setItem("token", res.data.token);
      localStorage.setItem("user", JSON.stringify(res.data.user));
      setUser(res.data.user);

      message.success("Registration successful");
      navigate("/");
    } catch (error) {
      message.error("Google sign up failed. Please try again.");
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <div className="register-page">
      <Row justify="center" align="middle" className="register-container">
        <Col xs={22} sm={20} md={12} lg={10} xl={8}>
          <Card className="register-card" variant="borderless">
            <div className="register-header">
              <div className="brand-section">
                <div className="brand-logo">D</div>
                <Title level={3} className="brand-name">Draptor</Title>
              </div>
              <Title level={4} className="register-title">
                Create account
              </Title>
              <Text className="register-subtitle">
                Join Draptor
              </Text>
            </div>

            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              validateTrigger={[]}
              requiredMark={false}
            >
              <Form.Item
                label="Full name"
                name="name"
                className="form-item"
              >
                <Input
                  placeholder="Enter your full name"
                  className="input-field"
                />
              </Form.Item>

              <Form.Item
                label="Email"
                name="email"
                className="form-item"
              >
                <Input
                  placeholder="you@example.com"
                  className="input-field"
                />
              </Form.Item>

              <Form.Item
                label="Mobile number"
                name="phone"
                className="form-item"
              >
                <Input
                  placeholder="9876543210"
                  className="input-field"
                  maxLength={10}
                />
              </Form.Item>

              <Form.Item
                label="Password"
                name="password"
                className="form-item"
              >
                <Input.Password
                  placeholder="Minimum 6 characters"
                  className="input-field"
                />
              </Form.Item>

              <Form.Item className="form-item">
                <Button
                  type="primary"
                  htmlType="button"
                  block
                  className="register-btn"
                  loading={loading}
                  onClick={handleSubmit}
                >
                  Sign up
                </Button>
              </Form.Item>

              <Divider className="custom-divider">Or</Divider>

              <Button
                block
                className="google-btn"
                loading={googleLoading}
                onClick={handleGoogleSignUp}
              >
                <img
                  src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                  alt="google"
                  className="google-icon"
                />
                Sign up with Google
              </Button>


              <div className="register-footer">
                <Text className="footer-text">
                  Already have an account?{" "}
                  <Link to="/login" className="register-link">
                    Sign in
                  </Link>
                </Text>
              </div>
            </Form>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default Register;